import { useState, type FormEvent } from 'react'
import {
  CUIDARI_ACCESS_GATE_COPY,
  buildIncompleteInviteNotice,
  buildInviteErrorNotice,
  buildInviteSuccessNotice,
  buildWaitlistErrorNotice,
  parseAccessGateInviteInput,
  type AccessGateNotice,
} from '@francisquiniai/cuidari-core/auth'
import { getEntryEmail, GrowthApiError } from '@/modules/growth/api'
import { GateBackButton, GateNoticeCard } from './gateUi'
import { icon, type InviteResult } from './shared'

export function InviteForm({ onBack }: { onBack: () => void }) {
  const [inviteInput, setInviteInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<AccessGateNotice | null>(null)
  const [result, setResult] = useState<InviteResult | null>(null)
  const copy = CUIDARI_ACCESS_GATE_COPY.invite

  async function submit(e: FormEvent) {
    e.preventDefault()
    const parsed = parseAccessGateInviteInput(
      inviteInput,
      typeof window !== 'undefined' ? window.location.origin : undefined,
    )
    setResult(null)

    if (!parsed) {
      setNotice(buildIncompleteInviteNotice())
      return
    }

    setBusy(true)
    setNotice(null)
    try {
      const entry = await getEntryEmail(parsed.entry, parsed.invite)
      setResult({ ...parsed, email: entry.email })
      setNotice(buildInviteSuccessNotice(entry.email))
    } catch (err) {
      if (err instanceof GrowthApiError) {
        setNotice(buildInviteErrorNotice({ status: err.status, code: err.code }))
      } else {
        setNotice(buildWaitlistErrorNotice({}))
      }
    } finally {
      setBusy(false)
    }
  }

  function continueWithInvite() {
    if (!result || typeof window === 'undefined') return
    const qs = new URLSearchParams({
      invite: result.invite,
      entry: result.entry,
    })
    window.location.assign(`/entrar?${qs.toString()}`)
  }

  return (
    <section className="login-card" data-testid="invite-form">
      <p className="login-kicker">{copy.kicker}</p>
      <h2>{copy.title}</h2>
      <p className="login-card__body">{copy.body}</p>

      <div className="login-invite-panel">
        <span>{icon('mail')}</span>
        <p>{copy.panel}</p>
      </div>

      {notice && (
        <GateNoticeCard
          notice={notice}
          testId={notice.tone === 'success' ? 'invite-success' : 'invite-error'}
        />
      )}

      <form onSubmit={submit} className="login-form">
        <label className="login-field">
          <span>{copy.inputLabel}</span>
          <textarea
            required
            placeholder={copy.inputPlaceholder}
            data-testid="invite-input"
            value={inviteInput}
            onChange={(e) => {
              setInviteInput(e.target.value)
              setNotice(null)
              setResult(null)
            }}
            rows={3}
          />
        </label>
        <button
          type="submit"
          className="login-button login-button--primary"
          disabled={busy}
          data-testid="invite-submit"
        >
          {busy && <span className="login-spinner" aria-hidden="true" />}
          <span>{busy ? copy.submitBusy : copy.submitIdle}</span>
        </button>
      </form>

      {result && (
        <button
          type="button"
          className="login-button login-button--secondary"
          onClick={continueWithInvite}
        >
          {icon('login')}
          <span>{copy.continueLabel}</span>
        </button>
      )}

      <GateBackButton onClick={onBack} />
    </section>
  )
}
